/* eslint-disable max-len */
import { useGetList, useNotify, useRefresh, useTranslate } from "react-admin";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as User from "providers/user";
import { useState } from "react";
import { CustomButton } from "./CustomButton";
import OrderEditItem from "./OrderEditItem";
import * as Axios from "providers/axios";

interface IOrderEditProps {
  orderId: string | undefined;
  data: any;
  isEditOpen: boolean;
  onClose: () => void;
}

export interface IEditedArtist {
  id: string;
  artistId: string;
}

const OrderEdit = ({ orderId, data, isEditOpen, onClose }: IOrderEditProps) => {
  const { permissions } = User.useUser();
  const translate = useTranslate();
  const refresh = useRefresh();
  const { api } = Axios.useAxios();
  const notify = useNotify();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [removedServices, setRemovedServices] = useState<string[]>([]);
  const [removedAddons, setRemovedAddons] = useState<string[]>([]);
  const [removedItems, setRemovedItems] = useState<string[]>([]);
  const [editedArtists, setEditedArtists] = useState<IEditedArtist[]>([]);
  const [editedAddOnArtists, setEditedAddOnArtists] = useState<IEditedArtist[]>(
    []
  );
  const { data: artistData } = useGetList("Artist", {
    filter: {
      user: {
        is: {
          joined_company_id: permissions?.companyId
        }
      },
      is_active: {
        equals: true
      }
    }
  });

  const artistChoices = artistData?.map((item: any) => {
    return {
      id: item?.id,
      name: `${item?.user?.first_name} ${item?.user?.last_name}`
    };
  });

  const everyRemoved = [...removedServices, ...removedAddons, ...removedItems];
  const everyEdited = [...editedArtists, ...editedAddOnArtists];

  const totalAmountAfterDelete = data
    ?.filter((i: any) => everyRemoved?.includes(i.id))
    ?.reduce((acc: number, curr: any) => {
      acc -= curr?.price ?? 0;
      return acc;
    }, data?.[0]?.order?.full_amount);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await api.post("/v1/appointments/update-order-items", {
        orderId,
        serviceIds: removedServices,
        addonIds: removedAddons,
        itemIds: removedItems,
        editedServiceArtists: editedArtists,
        editedAddOnArtists: editedAddOnArtists
      });
      refresh();
      onClose();
    } catch (e) {
      notify(translate("common.something_went_wrong"), {
        type: "error",
        undoable: false,
        anchorOrigin: { vertical: "bottom", horizontal: "center" }
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={isEditOpen}
      onClose={onClose}
      sx={{
        "& .MuiDialogContent-root": {
          padding: "0px"
        },
        "& .MuiDialog-paper": {
          borderRadius: "8px",
          backgroundColor: "#fff",
          maxWidth: "740px",
          width: "100%",
          marginX: "8px"
        }
      }}
    >
      <DialogTitle sx={{ display: "flex", padding: "4px 4px 0px 4px" }}>
        <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600] uppercase">
          {translate("order.edit_order")}
        </div>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div>
          <div className="hidden sm:flex justify-between items-center py-2 px-4 border-b sticky top-0 font-[600] bg-mono-white z-10">
            <div className="w-[36%]">{translate("common.name")}</div>
            <div className="w-[16%]">{translate("order.type")}</div>
            <div className="w-[36%] px-3">{translate("order.artist")}</div>
            <div className="w-[12%]">{translate("order.price")}</div>
            <div className="w-[78px]" />
          </div>
          {data?.map((item: any) => (
            <OrderEditItem
              key={item?.id}
              item={item}
              setRemovedServices={setRemovedServices}
              artistChoices={artistChoices}
              editedArtists={editedArtists}
              setEditedArtists={setEditedArtists}
              editedAddOnArtists={editedAddOnArtists}
              setEditedAddOnArtists={setEditedAddOnArtists}
              setRemovedAddons={setRemovedAddons}
              setRemovedItems={setRemovedItems}
              everyRemoved={everyRemoved}
              everyEdited={everyEdited}
            />
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <div className="w-full flex justify-between px-2">
          <div>
            <div className="text-[12px] text-mono-secondaryTextColor font-['Inter'] font-[400]">
              {translate("order.final_amount")}:
            </div>
            <div className="text-[16px] text-mono-primaryTextColor font-['Inter'] font-[400]">
              {everyRemoved?.length > 0 && (
                <span className="line-through decoration-error">
                  ${data?.[0]?.order?.full_amount}
                </span>
              )}
              {everyRemoved?.length > 0 && " / "}$
              {totalAmountAfterDelete > 0 ? totalAmountAfterDelete : 0}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <CustomButton
              variant="tertiary"
              text={translate("common.cancel")}
              onClick={onClose}
            />
            <CustomButton
              onClick={handleSave}
              text={translate("common.save")}
              disabled={
                isLoading || (!everyRemoved?.length && !everyEdited?.length)
              }
              endIcon={<div />}
              submitting={isLoading}
            />
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default OrderEdit;
